/**
 * Tabblad Persona's: de lijst, het CV van een persona, en het formulier
 * voor nieuw en bewerken (onderdelen/PersonaFormulier.js). Socrates is
 * niet te verwijderen: zonder discussieleider start geen les.
 */
import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { basis } from '../stijlen';
import { Portret } from '../../../components/Portret';
import { PersonaFormulier, LEGE_PERSONA } from '../onderdelen/PersonaFormulier';

export function Personas({ api, apiUrl }) {
  const [personas, setPersonas] = useState([]);
  const [laden, setLaden] = useState(false);
  const [gekozen, setGekozen] = useState(null);
  const [portretten, setPortretten] = useState({ basis: [], uploads: [] });
  const [nieuw, setNieuw] = useState(null);      // het formulier voor een nieuwe, of null
  const [bewerkt, setBewerkt] = useState(null);  // de bewerkte kopie van gekozen, of null

  const haalLijst = async () => {
    setLaden(true);
    try {
      const data = await api.haalPersonas();
      if (data.success) setPersonas(data.data);
    } catch (err) { console.error('Fout:', err); }
    finally { setLaden(false); }
  };

  const haalPortretten = async () => {
    try {
      const data = await api.haalPortretten();
      if (data.success) setPortretten(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { haalLijst(); haalPortretten(); }, []);

  const bekijk = async (id) => {
    try {
      const data = await api.haalPersona(id);
      if (data.success) setGekozen(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const maak = async () => {
    try {
      const data = await api.maakPersona(nieuw);
      if (data.success) { setNieuw(null); haalLijst(); }
    } catch (err) { console.error('Fout:', err); }
  };

  const slaOp = async () => {
    try {
      const data = await api.wijzigPersona(bewerkt);
      if (data.success) {
        setBewerkt(null);
        haalLijst();
        if (gekozen?.id === bewerkt.id) bekijk(bewerkt.id);
      }
    } catch (err) { console.error('Fout:', err); }
  };

  const verwijder = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze persona wilt verwijderen?')) return;
    try {
      const data = await api.verwijderPersona(id);
      if (data.success) { setGekozen(null); haalLijst(); }
    } catch (err) { console.error('Fout:', err); }
  };

  const formulier = (props) => (
    <PersonaFormulier api={api} apiUrl={apiUrl} portretten={portretten} onPortretGeupload={haalPortretten} {...props} />
  );

  const isSocrates = (p) => p.vast_type === 'discussieleider' || p.naam?.toLowerCase() === 'socrates';

  return (
    <div style={styles.splitView}>
      <div style={styles.listPanel}>
        <div style={styles.panelHeader}>
          <h2>Persona's</h2>
          <button onClick={() => setNieuw(LEGE_PERSONA)} style={styles.addBtn}><Plus size={16} /> Nieuwe Persona</button>
        </div>
        {nieuw && formulier({ titel: 'Nieuwe persona', waarde: nieuw, onWijzig: setNieuw, onOpslaan: maak, onAnnuleer: () => setNieuw(null) })}
        {laden ? <p>Laden...</p> : personas.length === 0 ? <p style={styles.empty}>Geen persona's.</p> : (
          <ul style={styles.list}>
            {personas.map((persona) => (
              <li key={persona.id} style={gekozen?.id === persona.id ? styles.listItemActive : styles.listItem} onClick={() => bekijk(persona.id)}>
                <span>{persona.naam}</span>
                {persona.vast_type && <span style={styles.badge}>{persona.vast_type}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div style={styles.detailPanel}>
        {!gekozen ? <p style={styles.empty}>Selecteer een persona</p> : bewerkt ? (
          formulier({ titel: 'Bewerken: ' + gekozen.naam, waarde: bewerkt, onWijzig: setBewerkt, onOpslaan: slaOp, onAnnuleer: () => setBewerkt(null) })
        ) : (
          <>
            <div style={styles.panelHeader}>
              <h2>
                <Portret apiUrl={apiUrl} naam={gekozen.portret} grootte={40} stijl={styles.portretKop} />
                {gekozen.naam}
              </h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setBewerkt({ ...gekozen })} style={styles.addBtn}>Bewerken</button>
                {!isSocrates(gekozen) && (
                  <button onClick={() => verwijder(gekozen.id)} style={styles.deleteBtn}><Trash2 size={16} /> Verwijderen</button>
                )}
              </div>
            </div>
            {gekozen.profiel && (
              <>
                <h3>Profiel</h3>
                <p>{gekozen.profiel}</p>
                <div style={{ height: '16px' }}></div>
              </>
            )}
            <h3>Korte omschrijving</h3>
            <p>{gekozen.korte_omschrijving}</p>
            <div style={{ height: '16px' }}></div>
            <div style={styles.infoGrid}>
              <div><strong>Geslacht:</strong> {gekozen.geslacht || '-'}</div>
              <div><strong>Leeftijd:</strong> {gekozen.leeftijd || '-'}</div>
              <div><strong>Geboorteplaats:</strong> {gekozen.geboorteplaats || '-'}</div>
              <div><strong>Woonplaats:</strong> {gekozen.woonplaats || '-'}</div>
              <div><strong>Samenlevingsvorm:</strong> {gekozen.samenlevingsvorm || '-'}</div>
              <div><strong>Werk:</strong> {gekozen.werk || '-'}</div>
              <div><strong>Opleiding:</strong> {gekozen.opleiding || '-'}</div>
              <div><strong>Inkomenspositie:</strong> {gekozen.inkomenspositie || '-'}</div>
              <div><strong>Migratieachtergrond:</strong> {gekozen.migratieachtergrond || '-'}</div>
            </div>
            {gekozen.bijzonderheden && (
              <>
                <h3>Bijzonderheden en interesses</h3>
                <p>{gekozen.bijzonderheden}</p>
                <div style={{ height: '16px' }}></div>
              </>
            )}
            <h3>Waarden & Overtuiging</h3>
            <p>{gekozen.achtergrond}</p>
            <div style={{ height: '16px' }}></div>
            <h3>Persoonlijkheid</h3>
            <p>{gekozen.persoonlijkheid}</p>
            <div style={{ height: '16px' }}></div>
            <h3>Spreekstijl</h3>
            <p>{gekozen.spreekstijl}</p>
            <div style={{ height: '16px' }}></div>
            {gekozen.vast_type && <p><strong>Agent-rol:</strong> {gekozen.vast_type}</p>}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  ...basis,
  portretKop: { verticalAlign: 'middle', marginRight: '10px' },
};
